<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import { scale } from "svelte/transition";
  import type { AlertType, AlertLink } from "$stores/alert";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import IconButton from "$components/+blocks/IconButton/IconButton.svelte";
  import { fade } from "svelte/transition";
  import {
    Cross,
    Spinner,
    CheckFilled,
    ExclamationSolid,
    CircleCrossFilled,
    CircleInfoFilled,
    Right,
  } from "$components/+blocks/OVIcon";
  import { tweened } from "svelte/motion";
  import { fadeSlide } from "app/transitions";

  export let id: string;
  export let type: AlertType = "success";
  export let title: string;
  export let description: string | undefined = undefined;
  export let link: AlertLink | undefined = undefined;
  export let maxWidthClass = "max-w-[400px]";
  export let persistent = false;
  export let progress = 0;

  // smoothly increase progress
  const progressWidth = tweened<number>(undefined, { duration: 300 });

  $: $progressWidth = Math.floor(progress);
  $: complete = $progressWidth == 100;

  const dispatch = createEventDispatcher();
  const AUTO_CLOSE_MS = 5000;

  // auto close after a few seconds
  if (!persistent) {
    onMount(() => {
      const timeout = setTimeout(() => {
        dispatch("close", id);
      }, AUTO_CLOSE_MS);
      return () => clearTimeout(timeout);
    });
  }

  // close notification after 100% complete
  $: if (complete) {
    setTimeout(() => dispatch("close", id), AUTO_CLOSE_MS);
  }

  let icon = CheckFilled;
  $: {
    if (complete) icon = CheckFilled;
    else if (type === "success") icon = CheckFilled;
    else if (type === "error") icon = CircleCrossFilled;
    else if (type === "information") icon = CircleInfoFilled;
    else if (type === "progress") icon = Spinner;
    else icon = ExclamationSolid;
  }
</script>

<div
  transition:fade|global={{ duration: 300 }}
  aria-live="polite"
  role={["warning", "error"].includes(type) ? "alert" : "status"}
  class="{maxWidthClass} flex items-start gap-2.5 rounded-lg border-[0.5px] border-black/25 bg-white p-3 shadow-[0_0_6px_rgba(0,0,0,0.07)] brightness-125 dark:border-white/5 dark:bg-neutral-800"
>
  {#key type}
    <span
      in:scale
      class:animate-spin={type === "progress" && !complete}
      class:text-orange-500={type === "progress" && !complete}
      class:text-green-500={type === "progress" && complete}
    >
      <OVIcon
        {icon}
        class={"transition-colors " +
          {
            success: "text-emerald-600 dark:text-emerald-500",
            error: "text-red-500",
            warning: "text-orange-500",
            progress: "",
            information: "text-neutral-500",
          }[type]}
      />
    </span>
  {/key}

  <div class="mr-10 flex flex-col space-y-3">
    <p class="text-[13px] leading-4 font-medium text-neutral-900 dark:text-neutral-100">
      {title}
    </p>

    {#if progress}
      <div
        class="mb-4 h-1.5 w-full rounded-full bg-neutral-200 dark:bg-neutral-700"
        in:fadeSlide={{ duration: 200 }}
        out:fadeSlide={{ delay: 1000, duration: 200 }}
      >
        <div
          class:bg-orange-600={progress && !complete}
          class:dark:bg-orange-500={progress && !complete}
          class:bg-green-600={complete}
          class:dark:bg-green-500={complete}
          class:glow={progress && !complete}
          class="relative h-1.5 rounded-full transition-colors"
          style:width="{$progressWidth}%"
        />
      </div>
    {/if}

    {#if description}
      <span class="text-[13px] font-medium text-neutral-500">{description}</span>
    {/if}

    {#if link}
      <a
        href={link.url}
        target="_self"
        class="text-[13px] font-medium text-sky-700 hover:text-sky-600 dark:text-sky-400 dark:hover:text-sky-500"
      >
        <span class="flex items-center">{link.text} <OVIcon icon={Right} class="ml-1" /> </span>
      </a>
    {/if}
  </div>
  <div class="absolute top-2.5 right-2.5">
    <IconButton
      variant="tertiary"
      size="small"
      icon={Cross}
      a11yLabel="Hide notification"
      on:click={() => dispatch("close")}
    />
  </div>
</div>

<style>
  .glow::before {
    content: "";
    position: absolute;
    border-radius: 20px;
    animation: animate-shadow 0.5s infinite;
    top: 0;
    right: 0px;
    width: 15px;
    height: 100%;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.4));
  }

  @keyframes animate-shadow {
    0% {
      box-shadow: 2px 0 10px 0 rgba(255, 255, 255, 0.34);
    }
    50% {
      box-shadow: 3px 0 10px 0 rgba(255, 255, 255, 0.36);
    }
    100% {
      box-shadow: 2px 0 10px 0 rgba(255, 255, 255, 0.35);
    }
  }
</style>
